import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UserService } from '@app/shared/services';
import { dateOnly } from '@app/shared/utils';
import moment from 'moment';
// import * as moment from 'moment-timezone';
import { Subscription } from 'rxjs';
import { SharedConfirmationDialogComponent } from '../shared-confirmation-dialog/shared-confirmation-dialog.component';
import { SharedVaultService } from '@app/shared/services/vault/shared-vault.service';
import { DateCompare } from '@app/shared/pipes/date-compare.pipe';

export const MY_FORMATS = {
  parse: {
    dateInput: 'LL',
  },
  display: {
    dateInput: 'LL',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';
@AutoUnsubscribe()
@Component({
  selector: 'accessibility-settings',
  templateUrl: './accessibility-settings.component.html',
  styleUrls: ['./accessibility-settings.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },

    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    DateCompare,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccessibilitySettingsComponent implements OnInit, OnDestroy {
  accessibleTypes = [
    { name: 'Now', color: '#2ECF99' },
    // { name: 'On future date', color: '#DEA000' },
    { name: 'Later', color: '#6766B4' },
  ];

  form: UntypedFormGroup;
  shareTypeDetails: { share_date: any; end_date: any; is_sealed: boolean; is_editable: boolean; result?: any };
  checkifDistributors: any;
  shareLaterDatechecked: boolean = !this.data?.shareDetails?.share_date ? true : false;
  subscription: Subscription = new Subscription();
  sharedsuccess: boolean = false;
  endDateSelected: boolean = false;
  viewMode;
  isPermissionTypeOwner: boolean = false;
  minDate: Date = new Date();
  // localTimeZone = moment.tz.guess();

  constructor(
    private dialogRef: MatDialogRef<AccessibilitySettingsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: UntypedFormBuilder,
    private dialog: MatDialog,
    private userService: UserService,
    private dateCompare: DateCompare,
    private cd: ChangeDetectorRef,
    private sharedVaultService: SharedVaultService
  ) {
    this.form = this.fb.group({
      accessibleDate: [this.todaysDate],
      permissionType: ['owner'],
      endDate: [],
      accessibleTypes: [[], Validators.required],
      editAccess: [false],
      receiveNotification: [false],
    });
  }

  ngOnInit(): void {
    if (this.data?.origin === 'contact' || this.data?.origin === 'password') {
      this.isPermissionTypeOwner = this.form?.get('permissionType').value == 'owner' ? true : false;
    }
    if (this.data?.shareDetails) {
      this.shareLaterDatechecked = !this.data?.shareDetails?.share_date;

      this.form.patchValue({
        accessibleDate: this.data?.shareDetails?.share_date ? moment(this.data?.shareDetails?.share_date).format('YYYY-MM-DD') : null,
        endDate: this.data?.shareDetails?.end_date ? moment(this.data?.shareDetails?.end_date).format('YYYY-MM-DD') : null,
        receiveNotification: this.data?.shareDetails?.is_sealed == 1 ? true : false,
        editAccess: this.data?.shareDetails?.is_editable == 1 ? true : false,
      });

      if (this.data?.shareDetails?.end_date != null && this.data.shareDetails?.end_date != '0000-00-00') {
        this.viewMode = 'view';
        this.endDateSelected = true;
      }

      this.setAccessibleType();
    } else {
      this.form.get('accessibleTypes').setValue(this.accessibleTypes[0]);
    }

    if (this.data?.fromAutoComplete && !this.data?.shareDetails) {
      this.shareLaterDatechecked = false;
    }

    this.checkIfUserHasDistribution();
  }

  changeAccessible() {
    if (this.accessibilityTypeNow) {
      this.form.get('accessibleDate').setValue(this.todaysDate);
      this.form.get('endDate').setValidators(null);
      this.form.get('accessibleDate').setValidators(Validators.required);
      this.shareLaterDatechecked = false;
    } else {
      this.form.get('accessibleDate').setValue(null);
      this.form.get('endDate').setValue(null);
      if (this.accessibilityTypeFuture) this.form.get('accessibleDate').setValidators(Validators.required);
      if (this.accessibilityTypeDistributor) {
        this.shareLaterDatechecked = true;
        this.form.get('editAccess').setValue(false);
        this.form.get('receiveNotification').setValue(false);
        this.form.get('accessibleDate').setValidators(null);
        this.form.get('endDate').setValidators(null);
      } else {
        this.shareLaterDatechecked = false;
      }
    }
    this.form.get('accessibleDate').updateValueAndValidity();
    this.form.get('endDate').updateValueAndValidity();
  }

  close() {
    this.dialogRef.close();
  }

  getAccessibilityType(): any {
    return this.form.get('accessibleTypes').value;
  }

  toggle(event) {
    this.endDateSelected = event.checked;
    this.form.get('endDate').setValue(null);
    if (this.endDateSelected) {
      this.form.get('endDate').setValidators(Validators.required);
    } else {
      this.form.get('endDate').setValidators(null);
    }
    this.form.get('endDate').updateValueAndValidity();
  }

  setAccessibleType() {
    // DISTRIBUTOR
    if (this.shareLaterDatechecked) {
      this.form.get('accessibleTypes').setValue(this.accessibleTypes[1]);
    } else {
      const accessibleDateValue = this.accessibleDate;
      if (accessibleDateValue && accessibleDateValue !== null) {
        const accessibleDate = new Date(accessibleDateValue);
        if (accessibleDate <= new Date(Date.now())) {
          this.form.get('accessibleTypes').setValue(this.accessibleTypes[0]);
        } 
        // we don't have  future date share, so we don't need to set
        // else {
        //   this.form.get('accessibleTypes').setValue(this.accessibleTypes[1]);
        // }
      } else {
        this.form.get('accessibleTypes').setValue(this.accessibleTypes[0]);
      }

    }
    this.cd.markForCheck();
  }

  get accessibilityTypeName() {
    const type = this.form.get('accessibleTypes').value;
    return type.name;
  }

  get accessibilityTypeNow() {
    return this.accessibilityTypeName == 'Now';
  }

  get accessibilityTypeFuture() {
    return this.accessibilityTypeName == 'On future date';
  }

  get accessibilityTypeDistributor() {
    return this.accessibilityTypeName == 'Later';
  }

  get todaysDate() {
    const date = moment();
    return date['_d'];
  }

  get tomorrowsDate() {
    const date = moment();
    const tom = date.add(1, 'days');
    return tom['_d'];
  }

  get accessibleDate() {
    return this.form.get('accessibleDate').value;
  }

  get endDate() {
    return this.form.get('endDate').value;
  }

  changePermissionType() {
    this.isPermissionTypeOwner = this.form?.get('permissionType').value == 'owner' ? true : false;
  }

  confirmShareTypeClicked() {
    if (this.data?.data?.generalPermission && this.isPermissionTypeOwner) {
      const body = {
        user_id: this.data.userData.id,
        register_type: this.data.data.origin,
        owner_type: 'owner',
      };
      if (body) {
        this.subscription.add(
          this.sharedVaultService.saveVaultRegisterMember(body).subscribe((res: any) => {
            if (res) {
              this.dialogRef.close({ isPermissionTypeOwner: true });
            }
          })
        );
      }
    } else {
      if (this.isPermissionTypeOwner) {
        const body = {
          user_id: this.data.userData.id,
          vault_user_form_id: this.data.data.file.vault_user_form_id,
          owner_type: 'owner',
        };
        if (body) {
          this.subscription.add(
            this.sharedVaultService.postNonVaultPermissionData(body).subscribe((res: any) => {
              if (res) {
                this.dialogRef.close({ isPermissionTypeOwner: true });
              }
            })
          );
        }
      } else {
        if (this.form.value.accessibleTypes.name !== 'Later') {
          if (
            (this.endDateSelected && this.endDate == null) ||
            (this.endDate != null && this.dateCompare.transform(this.form.get('endDate').value, this.form.get('accessibleDate').value))
          ) {
            return this.form.get('endDate').setErrors({ invalid: true });
          }
          // 1) IF FUTURE DATE
        }
        if (this.accessibilityTypeFuture && !this.checkifDistributors) {
          let formatedDate = moment(this.accessibleDate);
          let formattedEnddate = moment(this.endDate);
          this.shareTypeDetails = {
            share_date: this.shareLaterDatechecked ? null : formatedDate,
            end_date: formattedEnddate,
            is_sealed: this.form.get('receiveNotification').value,
            is_editable: this.form.get('editAccess').value,
          };
          this.dialogRef.close(this.shareTypeDetails);
          this.userService.setShareDone(true);
          this.sharedsuccess = true;
        }
        // 2) DISTRIBUTORS
        else if (this.accessibilityTypeDistributor && !this.checkifDistributors) {
          const dialogRef = this.dialog.open(SharedConfirmationDialogComponent, {
            width: '400px',
            data: {
              title: 'Confirm Sharing',
              materialIcon: 'warning',
              text: `Do you want to share ${this.data.sharedObjectType}(s) at a later date without adding a Data Distributor?`,
              proceedTitle: 'Share',
            },
          });
          this.subscription.add(
            dialogRef.afterClosed().subscribe((confirmation) => {
              if (confirmation) {
                let formatedDate = moment(this.accessibleDate);
                let formattedEnddate = moment(this.endDate);
                this.shareTypeDetails = {
                  share_date: this.shareLaterDatechecked ? null : formatedDate,
                  end_date: formattedEnddate,
                  is_sealed: this.form.get('receiveNotification').value,
                  is_editable: this.form.get('editAccess').value,
                };
                this.dialogRef.close(this.shareTypeDetails);
                this.userService.setShareDone(true);
                this.sharedsuccess = true;
              }
            })
          );
        } else {
          let formatedDate = moment(this.accessibleDate);
          let formattedEnddate = moment(this.endDate);
          this.shareTypeDetails = {
            share_date: this.shareLaterDatechecked ? null : formatedDate,
            end_date: formattedEnddate,
            is_sealed: this.form.get('receiveNotification').value,
            is_editable: this.form.get('editAccess').value,
          };
          this.dialogRef.close(this.shareTypeDetails);
          this.userService.setShareDone(true);
          this.sharedsuccess = true;
        }
      }
    }
  }

  checkIfUserHasDistribution() {
    this.subscription.add(
      this.userService.getIfUserHasDistribution().subscribe((res) => {
        this.checkifDistributors = res;
      })
    );
  }
  handleDateChange(value, control) {
    // this.form.controls['accessibleDate'].setValue(value);
  }
  handleDateChange2(value, control) {
    this.form.controls[control].setValue(value);
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }
}
